import { useState } from "react";
import { ProblemError } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import { Icon } from "../../ui/icons";
import { useLogInteraction } from "./use-household";
import {
  CONSENT_STATES,
  INTENT_TYPES,
  INTERACTION_CHANNELS,
  INTERACTION_DIRECTIONS,
  MARKETING_INTENTS,
  type ConsentStateInput,
  type IntentType,
  type Member,
} from "./types";

// The one in-scope 360 action: record a contact. A relationship intent
// (SERVICE/RENEWAL) needs no consent; a marketing intent (CROSS_SELL/WINBACK)
// surfaces the API's consent result honestly — a GRANTED contact without consent
// is refused (problem shown), and ABSENT_LOGGED_OPPORTUNITY logs an opportunity
// with no contact made (the no-spam discipline visible at the point of contact).
export function LogInteraction({
  householdId,
  members,
}: {
  householdId: string;
  members: Member[];
}) {
  const { t } = useT();
  const log = useLogInteraction(householdId);
  const [channel, setChannel] = useState<(typeof INTERACTION_CHANNELS)[number]>("PHONE");
  const [direction, setDirection] = useState<(typeof INTERACTION_DIRECTIONS)[number]>("OUTBOUND");
  const [intentType, setIntentType] = useState<IntentType>("SERVICE");
  const [partyId, setPartyId] = useState("");
  const [consentState, setConsentState] = useState<ConsentStateInput>("ABSENT_LOGGED_OPPORTUNITY");

  const isMarketing = MARKETING_INTENTS.includes(intentType);

  return (
    <form
      className="hh-log-form k-card"
      onSubmit={(e) => {
        e.preventDefault();
        log.mutate({
          householdId,
          partyId: partyId || undefined,
          channel,
          direction,
          intents: [
            {
              intentType,
              initiator: "ADVISOR",
              // Relationship intents need no consent; marketing carries the chosen state.
              consentState: isMarketing ? consentState : "NOT_REQUIRED",
            },
          ],
        });
      }}
    >
      <h3 className="k-tablecard__title" style={{ margin: "0 0 16px" }}>
        {t("hh.log.heading")}
      </h3>
      <label className="k-field">
        <span className="k-field__label">{t("hh.log.intent")}</span>
        <div className="k-select">
          <select
            className="k-select__native"
            aria-label={t("hh.log.intent")}
            value={intentType}
            onChange={(e) => setIntentType(e.target.value as IntentType)}
          >
            {INTENT_TYPES.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
          <span className="k-select__chevron">
            <Icon name="chevron-down" size={16} />
          </span>
        </div>
      </label>
      <label className="k-field">
        <span className="k-field__label">{t("hh.log.channel")}</span>
        <div className="k-select">
          <select
            className="k-select__native"
            aria-label={t("hh.log.channel")}
            value={channel}
            onChange={(e) => setChannel(e.target.value as typeof channel)}
          >
            {INTERACTION_CHANNELS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <span className="k-select__chevron">
            <Icon name="chevron-down" size={16} />
          </span>
        </div>
      </label>
      <label className="k-field">
        <span className="k-field__label">{t("hh.log.direction")}</span>
        <div className="k-select">
          <select
            className="k-select__native"
            aria-label={t("hh.log.direction")}
            value={direction}
            onChange={(e) => setDirection(e.target.value as typeof direction)}
          >
            {INTERACTION_DIRECTIONS.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
          <span className="k-select__chevron">
            <Icon name="chevron-down" size={16} />
          </span>
        </div>
      </label>
      <label className="k-field">
        <span className="k-field__label">Party</span>
        <div className="k-select">
          <select
            className="k-select__native"
            aria-label="Party"
            value={partyId}
            onChange={(e) => setPartyId(e.target.value)}
          >
            <option value="">—</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
              </option>
            ))}
          </select>
          <span className="k-select__chevron">
            <Icon name="chevron-down" size={16} />
          </span>
        </div>
      </label>
      {isMarketing && (
        <>
          <label className="k-field">
            <span className="k-field__label">{t("hh.log.consent")}</span>
            <div className="k-select">
              <select
                className="k-select__native"
                aria-label={t("hh.log.consent")}
                value={consentState}
                onChange={(e) => setConsentState(e.target.value as ConsentStateInput)}
              >
                {CONSENT_STATES.filter((s) => s !== "NOT_REQUIRED").map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <span className="k-select__chevron">
                <Icon name="chevron-down" size={16} />
              </span>
            </div>
          </label>
          {/* No-spam discipline at the point of contact — a calm amber notice, not alarm. */}
          <div className="k-note" style={{ padding: 14, marginBottom: 18 }}>
            <div className="k-note__row">
              <span className="k-note__icon k-note__icon--restricted">
                <Icon name="info" size={18} />
              </span>
              <p className="k-note__desc" style={{ margin: 0 }}>
                {t("hh.log.marketingHint")}
              </p>
            </div>
          </div>
        </>
      )}
      <button type="submit" className="k-btn k-btn--primary" disabled={log.isPending}>
        {t("hh.log.submit")}
      </button>
      {log.isSuccess && (
        <p role="status" style={{ fontSize: 13, color: "var(--k-green-solid)", marginTop: 12 }}>
          {t("hh.log.logged")}
        </p>
      )}
      {log.error instanceof ProblemError && (
        <p role="alert" style={{ fontSize: 13, color: "var(--k-muted)", marginTop: 12 }}>
          {log.error.problem.detail}
        </p>
      )}
    </form>
  );
}
